import { useState } from "react";
import { toast } from "sonner";
import { useCreateSupplier } from "../../hooks/useSuppliers";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { useCounties } from "@/shared/hooks/useMetaData";
import { PhoneNumberInput } from "@/shared/components/PhoneNumberInput";
import { useConfirm } from "@/shared/contexts/confirm-context";


const PAYMENT_METHODS = [
  "Cash",
  "Bank Transfer",
  "Mobile Money",
  "Check",
  "Credit",
];

const initialFormData = {
  supplierName: "",
  supplierCode: "",
  contactPerson: "",
  phoneNumber: "",
  email: "",
  address: "",
  county: "",
  town: "",
  taxId: "",
  bankAccount: "",
  mobileMoneyNumber: "",
  paymentMethod: "",
  creditLimit: 0,
  creditPeriodDays: 0,
  leadTimeDays: 0,
  contractStartDate: "",
  contractEndDate: "",
  notes: "",
};

export const CreateSupplierDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const createMutation = useCreateSupplier();
  const countiesQuery = useCounties();
  const confirm = useConfirm();

  const handleChange = (
    field: keyof typeof initialFormData,
    value: string | number,
  ) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Validate contract dates
    if (field === 'contractStartDate' || field === 'contractEndDate') {
      if (newFormData.contractStartDate && newFormData.contractEndDate) {
        const startDate = new Date(newFormData.contractStartDate);
        const endDate = new Date(newFormData.contractEndDate);
        
        if (endDate < startDate) {
          setFieldErrors((prev) => ({
            ...prev,
            contractEndDate: ['Contract end date must be after or equal to start date'],
          }));
          return;
        }
      }
    }
    
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    // Final validation for contract dates
    if (formData.contractStartDate && formData.contractEndDate) {
      const startDate = new Date(formData.contractStartDate);
      const endDate = new Date(formData.contractEndDate);
      
      if (endDate < startDate) {
        setFieldErrors({
          contractEndDate: ['Contract end date must be after or equal to start date'],
        });
        return;
      }
    }

    confirm({
      title: "Create supplier?",
      description: `“${formData.supplierName}” will be submitted and held for GM approval before it can be used in inputs or stock receipts.`,
      confirmLabel: "Create supplier",
      onConfirm: () =>
        createMutation
          .mutateAsync(formData as any)
          .then(() => {
            // Per spec #25 maker-checker: every new supplier lands in PendingApproval
            // and is not selectable in Input/Batch creation flows until the GM approves.
            toast.success(
              "Supplier submitted — pending GM approval before it can be used in inputs or stock receipts."
            );
            setFormData(initialFormData);
            setOpen(false);
          })
          .catch((error: any) => {
            if (error.response?.data?.errors) {
              setFieldErrors(error.response.data.errors);
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Supplier
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[90vh] w-[95vw] overflow-y-auto overflow-x-hidden sm:!max-w-[1400px] lg:w-[88vw] [&>*]:min-w-0"
      >
        <DialogHeader>
          <DialogTitle>Create New Supplier</DialogTitle>
          <DialogDescription>
            Add a new supplier to the system
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            New suppliers are submitted as <strong>pending</strong> and must be
            approved by the General Manager before they can be referenced on
            inputs or stock receipts.
          </div>

          {createMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {createMutation.error.message}
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="supplierName">
                  Supplier Name <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="supplierName"
                  value={formData.supplierName}
                  onChange={(e) => handleChange("supplierName", e.target.value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.supplierName && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.supplierName.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="supplierCode">
                  Supplier Code <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="supplierCode"
                  value={formData.supplierCode}
                  onChange={(e) => handleChange("supplierCode", e.target.value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.supplierCode && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.supplierCode.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="contactPerson">
                  Contact Person <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) =>
                    handleChange("contactPerson", e.target.value)
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.contactPerson && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.contactPerson.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="taxId">
                  Tax ID <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="taxId"
                  value={formData.taxId}
                  onChange={(e) => handleChange("taxId", e.target.value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.taxId && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.taxId.join(", ")}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <PhoneNumberInput
                value={formData.phoneNumber}
                onChange={(value) => handleChange("phoneNumber", value)}
                label="Phone Number"
                required
                disabled={createMutation.isPending}
                errors={fieldErrors.phoneNumber}
                placeholder="Enter phone number"
              />

              <Field>
                <FieldLabel htmlFor="email">
                  Email <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.email && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.email.join(", ")}
                  </div>
                )}
              </Field>

              <Field className="col-span-2">
                <FieldLabel htmlFor="address">
                  Address <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.address && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.address.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="county">
                  County <span className="text-red-500">*</span>
                </FieldLabel>
                {countiesQuery.isLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <Select
                    value={formData.county}
                    onValueChange={(value) => handleChange("county", value)}
                    disabled={createMutation.isPending}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select County" />
                    </SelectTrigger>
                    <SelectContent>
                      {countiesQuery.data?.map((county) => (
                        <SelectItem key={county.code} value={county.name}>
                          {county.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {fieldErrors.county && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.county.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="town">
                  Town <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="town"
                  value={formData.town}
                  onChange={(e) => handleChange("town", e.target.value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.town && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.town.join(", ")}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Financial Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Financial Information
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="paymentMethod">
                  Payment Method <span className="text-red-500">*</span>
                </FieldLabel>
                <Select
                  value={formData.paymentMethod}
                  onValueChange={(value) =>
                    handleChange("paymentMethod", value)
                  }
                  disabled={createMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Payment Method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method} value={method}>
                        {method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldErrors.paymentMethod && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.paymentMethod.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="creditLimit">
                  Credit Limit <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="creditLimit"
                  type="number"
                  value={formData.creditLimit}
                  onChange={(e) =>
                    handleChange("creditLimit", parseFloat(e.target.value) || 0)
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.creditLimit && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.creditLimit.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="creditPeriodDays">
                  Credit Period (Days) <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="creditPeriodDays"
                  type="number"
                  value={formData.creditPeriodDays}
                  onChange={(e) =>
                    handleChange(
                      "creditPeriodDays",
                      parseInt(e.target.value) || 0,
                    )
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.creditPeriodDays && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.creditPeriodDays.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="leadTimeDays">
                  Lead Time (Days) <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="leadTimeDays"
                  type="number"
                  value={formData.leadTimeDays}
                  onChange={(e) =>
                    handleChange("leadTimeDays", parseInt(e.target.value) || 0)
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.leadTimeDays && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.leadTimeDays.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="bankAccount">
                  Bank Account <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="bankAccount"
                  value={formData.bankAccount}
                  onChange={(e) => handleChange("bankAccount", e.target.value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.bankAccount && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.bankAccount.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="mobileMoneyNumber">
                  Mobile Money Number <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="mobileMoneyNumber"
                  value={formData.mobileMoneyNumber}
                  onChange={(e) =>
                    handleChange("mobileMoneyNumber", e.target.value)
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.mobileMoneyNumber && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.mobileMoneyNumber.join(", ")}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Contract Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contract Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="contractStartDate">
                  Contract Start Date <span className="text-red-500">*</span>
                </FieldLabel>
                <DatePicker
                  id="contractStartDate"
                  value={formData.contractStartDate}
                  onChange={(value) => handleChange("contractStartDate", value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.contractStartDate && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.contractStartDate.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="contractEndDate">
                  Contract End Date <span className="text-red-500">*</span>
                </FieldLabel>
                <DatePicker
                  id="contractEndDate"
                  value={formData.contractEndDate}
                  onChange={(value) => handleChange("contractEndDate", value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.contractEndDate && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.contractEndDate.join(", ")}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* Notes */}
          <Field>
            <FieldLabel htmlFor="notes">Notes</FieldLabel>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              disabled={createMutation.isPending}
              placeholder="Additional notes about this supplier"
              className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            />
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Supplier"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
